import React, { useState, useEffect } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { CloudConnectivity } from "./CloudConnectivityData";
import { GenericTag } from "@ui-components/Tag/GenericTag";

export interface CloudConnectivityViewModalProps {
    isOpen: boolean;
    isLoading: boolean;
    cloudConnectivity: CloudConnectivity | null;
    onClose: () => void;
  }
  
  export const CloudConnectivityViewModal: React.FC<CloudConnectivityViewModalProps> = ({
    isOpen,
    isLoading,
    cloudConnectivity,
    onClose,
  }) => {
    return (
      <GenericModal
        isOpen={isOpen}
        onClose={onClose}
        title="Cloud Connectivity Service Details"
        size="lg"
        overflowY="auto"
      >
        {isLoading ? (
          <div className="space-y-6">
            {[...Array(3)].map((_, idx) => (
              <div key={idx} className="grid grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i}>
                    <SkeletonBlock h={20} w="30%" className="mb-3" />
                    <SkeletonText lines={1} lineHeight={14} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          cloudConnectivity && (
            <div className="space-y-6">
              {/* Section 1: General Information */}
              <GenericCard
                isCollapsible={true}
                initialExpanded={true}
                headerContent={<h3 className="text-lg font-semibold text-semantic-text-primary">General Information</h3>}
                variant="primary"
                shadow="flat"
                hasBorder={true}
              >
                <div className="p-4">
                  <div className="grid grid-cols-3 gap-4">
                    <GenericInput
                      label="ID"
                      value={String(cloudConnectivity.id ?? "")}
                      readOnly
                    />
                    <GenericInput
                      label="Name"
                      value={cloudConnectivity.name ?? ""}
                      readOnly
                    />
                    <GenericInput
                      label="Subscription ID"
                      value={cloudConnectivity.subscriptionId ?? ""}
                      readOnly
                    />
                    <GenericInput
                      label="Mbps"
                      value={String(cloudConnectivity.mbps ?? "")}
                      readOnly
                    />
                    <GenericInput
                      label="VLAN"
                      value={String(cloudConnectivity.vlan ?? "")}
                      readOnly
                    />
                    <GenericInput
                      label="Automatic VLAN"
                      value={cloudConnectivity.automaticVlan ? "Yes" : "No"}
                      readOnly
                    />
                    <GenericInput
                      label="Terms"
                      value={String(cloudConnectivity.terms ?? "")}
                      readOnly
                    />
                    <GenericInput
                      label="Auto Renew"
                      value={cloudConnectivity.autoRenew === null ? "N/A" : (cloudConnectivity.autoRenew ? "Yes" : "No")}
                      readOnly
                    />
                    <GenericInput
                      label="Status"
                      value={cloudConnectivity.isDraft ? "Draft" : "Active"}
                      readOnly
                    />
                    <GenericInput
                      label="Kontrol Edge Port ID"
                      value={String(cloudConnectivity.kontrolEdgePortId ?? "")}
                      readOnly
                    />
                    <GenericInput
                      label="Kontrol Edge LAG ID"
                      value={cloudConnectivity.kontrolEdgeLagId ? String(cloudConnectivity.kontrolEdgeLagId) : "N/A"}
                      readOnly
                    />
                    <GenericInput
                      label="Provider"
                      value={cloudConnectivity.provider ?? ""}
                      readOnly
                    />
                    <GenericInput
                      label="Provider ID"
                      value={cloudConnectivity.providerId ?? ""}
                      readOnly
                    />
                    <GenericInput
                      label="Provider Account"
                      value={String(cloudConnectivity.providerAccount ?? "")}
                      readOnly
                    />
                    <GenericInput
                      label="Cloud"
                      value={cloudConnectivity.cloud ?? ""}
                      readOnly
                    />
                    <GenericInput
                      label="Account"
                      value={cloudConnectivity.account ?? ""}
                      readOnly
                    />
                    <GenericInput
                      label="Created At"
                      value={cloudConnectivity.createdAt ? new Date(cloudConnectivity.createdAt).toLocaleString() : ""}
                      readOnly
                    />
                    <GenericInput
                      label="Updated At"
                      value={cloudConnectivity.updatedAt ? new Date(cloudConnectivity.updatedAt).toLocaleString() : ""}
                      readOnly
                    />
                    <div className="col-span-3">
                      <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                        Tags
                      </label>
                      {cloudConnectivity.tag && cloudConnectivity.tag.length > 0 ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          {cloudConnectivity.tag.map((tag, index) => (
                            <GenericTag
                              key={index}
                              variant="neutral"
                              label={tag}
                              size="small"
                            />
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-semantic-text-secondary">-</span>
                      )}
                    </div>
                    {cloudConnectivity.monitoring && (
                      <>
                        <div className="col-span-3">
                          <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                            Monitoring
                          </label>
                          <div className="grid grid-cols-2 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
                            <GenericInput
                              label="Enable"
                              value={cloudConnectivity.monitoring.enable ? "Yes" : "No"}
                              readOnly
                            />
                            <GenericInput
                              label="Maintenance Mode"
                              value={cloudConnectivity.monitoring.maintenance_mode ? "Yes" : "No"}
                              readOnly
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </GenericCard>
  
              {/* Section 2: Kontrol Edge Port */}
              {cloudConnectivity.kontrolEdgePort && (
                <GenericCard
                  isCollapsible={true}
                  initialExpanded={true}
                  headerContent={<h3 className="text-lg font-semibold text-semantic-text-primary">Kontrol Edge Port</h3>}
                  variant="primary"
                  shadow="flat"
                  hasBorder={true}
                >
                  <div className="p-4">
                    <div className="grid grid-cols-3 gap-4">
                      <GenericInput
                        label="Port ID"
                        value={String(cloudConnectivity.kontrolEdgePort.id ?? "")}
                        readOnly
                      />
                      <GenericInput
                        label="Sub ID"
                        value={cloudConnectivity.kontrolEdgePort.subid ?? ""}
                        readOnly
                      />
                      <GenericInput
                        label="Port Name"
                        value={cloudConnectivity.kontrolEdgePort.name ?? ""}
                        readOnly
                      />
                      <GenericInput
                        label="Tenant ID"
                        value={String(cloudConnectivity.kontrolEdgePort.tenantId ?? "")}
                        readOnly
                      />
                      <GenericInput
                        label="Hardware ID"
                        value={cloudConnectivity.kontrolEdgePort.hardwareId ?? ""}
                        readOnly
                      />
                      <GenericInput
                        label="Capacity (Mbps)"
                        value={String(cloudConnectivity.kontrolEdgePort.capacityMbps ?? "")}
                        readOnly
                      />
                      <GenericInput
                        label="Datacenter ID"
                        value={cloudConnectivity.kontrolEdgePort.datacenterId ?? ""}
                        readOnly
                      />
                      {cloudConnectivity.kontrolEdgePort.tenant && (
                        <>
                          <div className="col-span-3">
                            <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                              Tenant Information
                            </label>
                            <div className="grid grid-cols-4 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
                              <GenericInput
                                label="Tenant ID"
                                value={String(cloudConnectivity.kontrolEdgePort.tenant.id ?? "")}
                                readOnly
                              />
                              <GenericInput
                                label="Name"
                                value={cloudConnectivity.kontrolEdgePort.tenant.Name ?? ""}
                                readOnly
                              />
                              <GenericInput
                                label="Display Name"
                                value={cloudConnectivity.kontrolEdgePort.tenant.nameDisplay ?? ""}
                                readOnly
                              />
                              <GenericInput
                                label="Chargebee Customer ID"
                                value={cloudConnectivity.kontrolEdgePort.tenant.chargebeeCustomerId ?? ""}
                                readOnly
                              />
                            </div>
                          </div>
                        </>
                      )}
                      {cloudConnectivity.kontrolEdgePort.datacenter && (
                        <>
                          <div className="col-span-3">
                            <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                              Datacenter Information
                            </label>
                            <div className="grid grid-cols-4 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
                              <GenericInput
                                label="Datacenter ID"
                                value={cloudConnectivity.kontrolEdgePort.datacenter.datacenterId ?? ""}
                                readOnly
                              />
                              <GenericInput
                                label="Name"
                                value={cloudConnectivity.kontrolEdgePort.datacenter.name ?? ""}
                                readOnly
                              />
                              <GenericInput
                                label="Display Name"
                                value={cloudConnectivity.kontrolEdgePort.datacenter.displayName ?? ""}
                                readOnly
                              />
                              <GenericInput
                                label="City"
                                value={cloudConnectivity.kontrolEdgePort.datacenter.city ?? ""}
                                readOnly
                              />
                            </div>
                          </div>
                        </>
                      )}
                      {cloudConnectivity.kontrolEdgePort.hardware && (
                        <>
                          <div className="col-span-3">
                            <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                              Hardware Information
                            </label>
                            <div className="grid grid-cols-3 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
                              <GenericInput
                                label="Hardware ID"
                                value={String(cloudConnectivity.kontrolEdgePort.hardware.id ?? "")}
                                readOnly
                              />
                              <GenericInput
                                label="Hostname"
                                value={cloudConnectivity.kontrolEdgePort.hardware.hostname ?? ""}
                                readOnly
                              />
                              <GenericInput
                                label="Model"
                                value={cloudConnectivity.kontrolEdgePort.hardware.model ?? ""}
                                readOnly
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </GenericCard>
              )}
  
              {/* Section 3: Odoo Metadata */}
              {cloudConnectivity.odooMetadata && (
                <GenericCard
                  isCollapsible={true}
                  initialExpanded={true}
                  headerContent={<h3 className="text-lg font-semibold text-semantic-text-primary">Odoo Metadata</h3>}
                  variant="primary"
                  shadow="flat"
                  hasBorder={true}
                >
                  <div className="p-4">
                    <div className="grid grid-cols-4 gap-4">
                      <GenericInput
                        label="Line ID"
                        value={String(cloudConnectivity.odooMetadata.lineId ?? "")}
                        readOnly
                      />
                      <GenericInput
                        label="Order ID"
                        value={String(cloudConnectivity.odooMetadata.orderId ?? "")}
                        readOnly
                      />
                      <GenericInput
                        label="Correlation"
                        value={cloudConnectivity.odooMetadata.correlation ?? ""}
                        readOnly
                      />
                      <GenericInput
                        label="Parent Correlation"
                        value={cloudConnectivity.odooMetadata.parentCorrelation ?? ""}
                        readOnly
                      />
                    </div>
                  </div>
                </GenericCard>
              )}
            </div>
          )
        )}
  
        <div className="flex justify-end mt-6">
          <GenericButton label="Close" variant="secondary" onClick={onClose} />
        </div>
      </GenericModal>
    );
  };
  
  export interface EditForm {
    name: string;
    mbps: number | null;
    tag: string[];
  }
  
  export interface CreateForm {
    kontrolEdgePortId: number | null;
    mbps: number | null;
    vlan: number | null;
    automaticVlan: boolean | null;
    subscriptionId: string;
    terms: number | null;
    name: string;
    tag: string[];
    provider: string;
    providerId: string;
    providerAccount: number | null;
    cloud: string;
    account: string;
    monitoring: {
      enable: boolean | null;
      maintenance_mode: boolean | null;
    };
  }
  
  export interface CloudConnectivityEditModalProps {
    isOpen: boolean;
    editMode: boolean;
    isLoading: boolean;
    cloudConnectivity: CloudConnectivity | null;
    editForm: EditForm;
    createForm: CreateForm;
    portOptions: Array<{ id: number; label: string }>;
    hasChanges: boolean;
    isFormValid: boolean;
    isSaving: boolean;
    isCreating: boolean;
    onClose: () => void;
    onSave: () => void;
    onCreate: () => void;
    onEditFieldChange: (field: "name" | "mbps" | "tag", value: string | number | string[]) => void;
    onCreateFieldChange: (field: string, value: string | number | boolean | null | string[] | { enable: boolean | null; maintenance_mode: boolean | null }) => void;
    onPortSelect: (portLabel: string) => void;
    getPortLabelById: (id: number | null) => string;
  }
  
  export const CloudConnectivityEditModal: React.FC<CloudConnectivityEditModalProps> = ({
    isOpen,
    editMode,
    isLoading,
    cloudConnectivity,
    editForm,
    createForm,
    portOptions,
    hasChanges,
    isFormValid,
    isSaving,
    isCreating,
    onClose,
    onSave,
    onCreate,
    onEditFieldChange,
    onCreateFieldChange,
    onPortSelect,
    getPortLabelById,
  }) => {
    // Local state for tag input to allow typing commas (edit mode)
    const [tagInput, setTagInput] = useState<string>("");
    // Local state for tag input in create mode
    const [createTagInput, setCreateTagInput] = useState<string>("");
  
    // Initialize tagInput when modal opens or when editForm.tag changes (for when data is loaded)
    useEffect(() => {
      if (isOpen && editMode) {
        if (editForm.tag && editForm.tag.length > 0) {
          setTagInput(editForm.tag.join(", "));
        } else {
          setTagInput("");
        }
      } else if (isOpen && !editMode && createForm.tag) {
        setCreateTagInput(createForm.tag.join(", "));
      } else if (!isOpen) {
        setTagInput("");
        setCreateTagInput("");
      }
    }, [isOpen, editMode, editForm.tag]); // Include editForm.tag to sync when data is loaded
  
    // Handle tag input change - keep as string to allow typing commas (edit mode)
    const handleTagInputChange = (value: string) => {
      setTagInput(value);
      // Convert to array for parent state (for change detection), but keep the raw string in local state
      // This allows the user to type commas without them being removed
      const tags = value.split(",").map(t => t.trim()).filter(Boolean);
      onEditFieldChange("tag", tags);
    };
  
    // Handle tag input change for create mode
    const handleCreateTagInputChange = (value: string) => {
      setCreateTagInput(value);
      // Convert to array for parent state
      const tags = value.split(",").map(t => t.trim()).filter(Boolean);
      onCreateFieldChange("tag", tags);
    };
  
    return (
      <GenericModal
        isOpen={isOpen}
        onClose={onClose}
        title={editMode ? "Edit Cloud Connectivity Service" : "Add Cloud Connectivity Service"}
        size="lg"
        overflowY="auto"
      >
        {editMode ? (
          isLoading ? (
            <div className="space-y-6">
              {[...Array(3)].map((_, idx) => (
                <div key={idx} className="grid grid-cols-3 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i}>
                      <SkeletonBlock h={20} w="30%" className="mb-3" />
                      <SkeletonText lines={1} lineHeight={14} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : cloudConnectivity ? (
            <div className="space-y-6">
              {/* Section 1: General Information */}
              <GenericCard
                isCollapsible={true}
                initialExpanded={true}
                headerContent={<h3 className="text-lg font-semibold text-semantic-text-primary">General Information</h3>}
                variant="primary"
                shadow="flat"
                hasBorder={true}
              >
                <div className="p-4">
                  <div className="grid grid-cols-3 gap-4">
                    <GenericInput
                      label="ID"
                      value={String(cloudConnectivity.id ?? "")}
                      readOnly
                    />
                    <GenericInput
                      label="Name"
                      value={editForm.name}
                      onChange={(value) => onEditFieldChange("name", value)}
                    />
                    <GenericInput
                      label="Subscription ID"
                      value={cloudConnectivity.subscriptionId ?? ""}
                      readOnly
                    />
                    <GenericInput
                      label="Mbps"
                      value={editForm.mbps ? String(editForm.mbps) : ""}
                      onChange={(value) => onEditFieldChange("mbps", value)}
                      type="number"
                    />
                    <GenericInput
                      label="VLAN"
                      value={String(cloudConnectivity.vlan ?? "")}
                      readOnly
                    />
                    <GenericInput
                      label="Automatic VLAN"
                      value={cloudConnectivity.automaticVlan ? "Yes" : "No"}
                      readOnly
                    />
                    <GenericInput
                      label="Terms"
                      value={String(cloudConnectivity.terms ?? "")}
                      readOnly
                    />
                    <GenericInput
                      label="Auto Renew"
                      value={cloudConnectivity.autoRenew === null ? "N/A" : (cloudConnectivity.autoRenew ? "Yes" : "No")}
                      readOnly
                    />
                    <GenericInput
                      label="Status"
                      value={cloudConnectivity.isDraft ? "Draft" : "Active"}
                      readOnly
                    />
                    <GenericInput
                      label="Kontrol Edge Port ID"
                      value={String(cloudConnectivity.kontrolEdgePortId ?? "")}
                      readOnly
                    />
                    <GenericInput
                      label="Kontrol Edge LAG ID"
                      value={cloudConnectivity.kontrolEdgeLagId ? String(cloudConnectivity.kontrolEdgeLagId) : "N/A"}
                      readOnly
                    />
                    <GenericInput
                      label="Provider"
                      value={cloudConnectivity.provider ?? ""}
                      readOnly
                    />
                    <GenericInput
                      label="Provider ID"
                      value={cloudConnectivity.providerId ?? ""}
                      readOnly
                    />
                    <GenericInput
                      label="Provider Account"
                      value={cloudConnectivity.providerAccount ? String(cloudConnectivity.providerAccount) : "N/A"}
                      readOnly
                    />
                    <GenericInput
                      label="Cloud"
                      value={cloudConnectivity.cloud ?? ""}
                      readOnly
                    />
                    <GenericInput
                      label="Account"
                      value={cloudConnectivity.account ?? ""}
                      readOnly
                    />
                    <GenericInput
                      label="Created At"
                      value={cloudConnectivity.createdAt ? new Date(cloudConnectivity.createdAt).toLocaleString() : ""}
                      readOnly
                    />
                    <GenericInput
                      label="Updated At"
                      value={cloudConnectivity.updatedAt ? new Date(cloudConnectivity.updatedAt).toLocaleString() : ""}
                      readOnly
                    />
                    <div className="col-span-3">
                      <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                        Tag *
                      </label>
                      <GenericInput
                      value={tagInput}
                      onChange={handleTagInputChange}
                      placeholder={
                        cloudConnectivity?.tag && cloudConnectivity.tag.length > 0
                          ? `Current tags: ${cloudConnectivity.tag.join(", ")}`
                          : "Enter tags separated by commas"
                      }
                    />
                    </div>
                    {cloudConnectivity.monitoring && (
                      <>
                        <div className="col-span-3">
                          <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                            Monitoring
                          </label>
                          <div className="grid grid-cols-2 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
                            <GenericInput
                              label="Enable"
                              value={cloudConnectivity.monitoring.enable ? "Yes" : "No"}
                              readOnly
                            />
                            <GenericInput
                              label="Maintenance Mode"
                              value={cloudConnectivity.monitoring.maintenance_mode ? "Yes" : "No"}
                              readOnly
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </GenericCard>
  
              {/* Section 2: Kontrol Edge Port */}
              {cloudConnectivity.kontrolEdgePort && (
                <GenericCard
                  isCollapsible={true}
                  initialExpanded={true}
                  headerContent={<h3 className="text-lg font-semibold text-semantic-text-primary">Kontrol Edge Port</h3>}
                  variant="primary"
                  shadow="flat"
                  hasBorder={true}
                >
                  <div className="p-4">
                    <div className="grid grid-cols-3 gap-4">
                      <GenericInput
                        label="Port ID"
                        value={String(cloudConnectivity.kontrolEdgePort.id ?? "")}
                        readOnly
                      />
                      <GenericInput
                        label="Sub ID"
                        value={cloudConnectivity.kontrolEdgePort.subid ?? ""}
                        readOnly
                      />
                      <GenericInput
                        label="Port Name"
                        value={cloudConnectivity.kontrolEdgePort.name ?? ""}
                        readOnly
                      />
                      <GenericInput
                        label="Tenant ID"
                        value={String(cloudConnectivity.kontrolEdgePort.tenantId ?? "")}
                        readOnly
                      />
                      <GenericInput
                        label="Hardware ID"
                        value={cloudConnectivity.kontrolEdgePort.hardwareId ?? ""}
                        readOnly
                      />
                      <GenericInput
                        label="Capacity (Mbps)"
                        value={String(cloudConnectivity.kontrolEdgePort.capacityMbps ?? "")}
                        readOnly
                      />
                      <GenericInput
                        label="Datacenter ID"
                        value={cloudConnectivity.kontrolEdgePort.datacenterId ?? ""}
                        readOnly
                      />
                      {cloudConnectivity.kontrolEdgePort.tenant && (
                        <>
                          <div className="col-span-3">
                            <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                              Tenant Information
                            </label>
                            <div className="grid grid-cols-4 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
                              <GenericInput
                                label="Tenant ID"
                                value={String(cloudConnectivity.kontrolEdgePort.tenant.id ?? "")}
                                readOnly
                              />
                              <GenericInput
                                label="Name"
                                value={cloudConnectivity.kontrolEdgePort.tenant.Name ?? ""}
                                readOnly
                              />
                              <GenericInput
                                label="Display Name"
                                value={cloudConnectivity.kontrolEdgePort.tenant.nameDisplay ?? ""}
                                readOnly
                              />
                              <GenericInput
                                label="Chargebee Customer ID"
                                value={cloudConnectivity.kontrolEdgePort.tenant.chargebeeCustomerId ?? ""}
                                readOnly
                              />
                            </div>
                          </div>
                        </>
                      )}
                      {cloudConnectivity.kontrolEdgePort.datacenter && (
                        <>
                          <div className="col-span-3">
                            <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                              Datacenter Information
                            </label>
                            <div className="grid grid-cols-4 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
                              <GenericInput
                                label="Datacenter ID"
                                value={cloudConnectivity.kontrolEdgePort.datacenter.datacenterId ?? ""}
                                readOnly
                              />
                              <GenericInput
                                label="Name"
                                value={cloudConnectivity.kontrolEdgePort.datacenter.name ?? ""}
                                readOnly
                              />
                              <GenericInput
                                label="Display Name"
                                value={cloudConnectivity.kontrolEdgePort.datacenter.displayName ?? ""}
                                readOnly
                              />
                              <GenericInput
                                label="City"
                                value={cloudConnectivity.kontrolEdgePort.datacenter.city ?? ""}
                                readOnly
                              />
                            </div>
                          </div>
                        </>
                      )}
                      {cloudConnectivity.kontrolEdgePort.hardware && (
                        <>
                          <div className="col-span-3">
                            <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                              Hardware Information
                            </label>
                            <div className="grid grid-cols-3 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
                              <GenericInput
                                label="Hardware ID"
                                value={String(cloudConnectivity.kontrolEdgePort.hardware.id ?? "")}
                                readOnly
                              />
                              <GenericInput
                                label="Hostname"
                                value={cloudConnectivity.kontrolEdgePort.hardware.hostname ?? ""}
                                readOnly
                              />
                              <GenericInput
                                label="Model"
                                value={cloudConnectivity.kontrolEdgePort.hardware.model ?? ""}
                                readOnly
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </GenericCard>
              )}
  
              {/* Section 3: Odoo Metadata */}
              {cloudConnectivity.odooMetadata && (
                <GenericCard
                  isCollapsible={true}
                  initialExpanded={true}
                  headerContent={<h3 className="text-lg font-semibold text-semantic-text-primary">Odoo Metadata</h3>}
                  variant="primary"
                  shadow="flat"
                  hasBorder={true}
                >
                  <div className="p-4">
                    <div className="grid grid-cols-4 gap-4">
                      <GenericInput
                        label="Line ID"
                        value={String(cloudConnectivity.odooMetadata.lineId ?? "")}
                        readOnly
                      />
                      <GenericInput
                        label="Order ID"
                        value={String(cloudConnectivity.odooMetadata.orderId ?? "")}
                        readOnly
                      />
                      <GenericInput
                        label="Correlation"
                        value={cloudConnectivity.odooMetadata.correlation ?? ""}
                        readOnly
                      />
                      <GenericInput
                        label="Parent Correlation"
                        value={cloudConnectivity.odooMetadata.parentCorrelation ?? ""}
                        readOnly
                      />
                    </div>
                  </div>
                </GenericCard>
              )}
            </div>
          ) : null
        ) : (
          <div className="space-y-6">
            {/* Kontrol Edge Port Selection */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                Kontrol Edge Port *
              </label>
              <GenericDropdown
                items={portOptions.map((p) => p.label)}
                value={getPortLabelById(createForm.kontrolEdgePortId)}
                defaultLabel="Select Port"
                onSelect={onPortSelect}
                minWidthClass="min-w-[300px]"
                maxWidthClass="max-w-[400px]"
              />
            </div>
  
            {/* Dividing line */}
            <div className="border-t border-semantic-border-secondary my-4"></div>
  
            {/* General Information */}
            <GenericCard
              isCollapsible={true}
              initialExpanded={true}
              headerContent={<h3 className="text-lg font-semibold text-semantic-text-primary">General Information</h3>}
              variant="primary"
              shadow="flat"
              hasBorder={true}
            >
              <div className="p-4">
                <div className="grid grid-cols-3 gap-4">
                  <GenericInput
                    label="Name *"
                    value={createForm.name}
                    onChange={(value) => onCreateFieldChange("name", value)}
                  />
                  <GenericInput
                    label="Subscription ID *"
                    value={createForm.subscriptionId}
                    onChange={(value) => onCreateFieldChange("subscriptionId", value)}
                  />
                  <GenericInput
                    label="Mbps *"
                    value={createForm.mbps ? String(createForm.mbps) : ""}
                    onChange={(value) => onCreateFieldChange("mbps", value ? Number(value) : null)}
                    type="number"
                  />
                  <GenericInput
                    label="VLAN *"
                    value={createForm.vlan ? String(createForm.vlan) : ""}
                    onChange={(value) => onCreateFieldChange("vlan", value ? Number(value) : null)}
                    type="number"
                  />
                  <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                      Automatic VLAN *
                    </label>
                    <GenericDropdown
                      items={["Yes", "No"]}
                      value={createForm.automaticVlan === null ? "" : (createForm.automaticVlan ? "Yes" : "No")}
                      defaultLabel="Select Automatic VLAN"
                      onSelect={(val) => onCreateFieldChange("automaticVlan", val === "Yes")}
                      minWidthClass="min-w-[180px]"
                      maxWidthClass="max-w-[240px]"
                    />
                  </div>
                  <GenericInput
                    label="Terms *"
                    value={createForm.terms ? String(createForm.terms) : ""}
                    onChange={(value) => onCreateFieldChange("terms", value ? Number(value) : null)}
                    type="number"
                  />
                  <GenericInput
                    label="Provider *"
                    value={createForm.provider}
                    onChange={(value) => onCreateFieldChange("provider", value)}
                  />
                  <GenericInput
                    label="Provider ID *"
                    value={createForm.providerId}
                    onChange={(value) => onCreateFieldChange("providerId", value)}
                  />
                  <GenericInput
                    label="Provider Account *"
                    value={createForm.providerAccount ? String(createForm.providerAccount) : ""}
                    onChange={(value) => onCreateFieldChange("providerAccount", value ? Number(value) : null)}
                    type="number"
                  />
                  <GenericInput
                    label="Cloud *"
                    value={createForm.cloud}
                    onChange={(value) => onCreateFieldChange("cloud", value)}
                  />
                  <GenericInput
                    label="Account *"
                    value={createForm.account}
                    onChange={(value) => onCreateFieldChange("account", value)}
                  />
                  <div className="col-span-3">
                    <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                      Tag
                    </label>
                    <GenericInput
                      value={createTagInput}
                      onChange={handleCreateTagInputChange}
                      placeholder="Enter tags separated by commas"
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                      Monitoring *
                    </label>
                    <div className="grid grid-cols-2 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
                      <div className="flex flex-col">
                        <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                          Enable *
                        </label>
                        <GenericDropdown
                          items={["Yes", "No"]}
                          value={createForm.monitoring.enable === null ? "" : (createForm.monitoring.enable ? "Yes" : "No")}
                          defaultLabel="Select Enable"
                          onSelect={(val) => onCreateFieldChange("monitoring", {
                            ...createForm.monitoring,
                            enable: val === "Yes",
                          })}
                          minWidthClass="min-w-[180px]"
                          maxWidthClass="max-w-[240px]"
                        />
                      </div>
                      <div className="flex flex-col">
                        <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                          Maintenance Mode *
                        </label>
                        <GenericDropdown
                          items={["Yes", "No"]}
                          value={createForm.monitoring.maintenance_mode === null ? "" : (createForm.monitoring.maintenance_mode ? "Yes" : "No")}
                          defaultLabel="Select Maintenance Mode"
                          onSelect={(val) => onCreateFieldChange("monitoring", {
                            ...createForm.monitoring,
                            maintenance_mode: val === "Yes",
                          })}
                          minWidthClass="min-w-[180px]"
                          maxWidthClass="max-w-[240px]"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </GenericCard>
          </div>
        )}
  
        <div className="flex justify-end space-x-2 mt-6">
          <GenericButton
            label="Cancel"
            variant="secondary"
            onClick={onClose}
            disabled={isSaving || isCreating}
          />
          {editMode ? (
            <GenericButton
              label="Save"
              variant="brand"
              onClick={onSave}
              disabled={!hasChanges || isSaving}
              loading={isSaving}
            />
          ) : (
            <GenericButton
              label="Create"
              variant="brand"
              onClick={onCreate}
              disabled={!isFormValid || isCreating}
              loading={isCreating}
            />
          )}
        </div>
      </GenericModal>
    );
  };
  
  